"use server";

import { auth } from "@/auth";
import { requireRole } from "@/lib/permissions";
import { GRP_ROLE_ADMIN } from "@/lib/grp";
import { validateParams } from "@/lib/utils";
import z from "zod";
import type { ActionResult } from "@/types/server-action";
import { prisma } from "@/prisma";
import { logActivity } from "@/app/lib/actions/activity-log";
import { ensureDraftPurgeSetting } from "@/lib/cat/draft-purge-settings";

const saveDraftPurgeSettingSchema = z.object({
  ttlDays: z.coerce.number().int().min(1).max(365),
});

export async function getDraftPurgeSettingAction(): Promise<
  ActionResult<{ ttlDays: number; updatedAt: string | null }>
> {
  try {
    const session = await auth();
    if (!session) {
      return { success: false, message: "Non authentifié." };
    }

    const { authorized } = requireRole(session, [GRP_ROLE_ADMIN]);
    if (!authorized) {
      return { success: false, message: "Accès refusé." };
    }

    const ttlDays = await ensureDraftPurgeSetting();
    const setting = await prisma.draftPurgeSetting.findUnique({
      where: { id: 1 },
      select: { updatedAt: true },
    });

    return {
      success: true,
      data: {
        ttlDays,
        updatedAt: setting?.updatedAt.toISOString() ?? null,
      },
    };
  } catch (error) {
    console.error("[getDraftPurgeSettingAction]", error);
    return { success: false, message: "Une erreur serveur est survenue." };
  }
}

export async function saveDraftPurgeSettingAction(
  params: z.infer<typeof saveDraftPurgeSettingSchema>,
): Promise<ActionResult<{ ttlDays: number }>> {
  try {
    const session = await auth();
    if (!session?.user?.id) {
      return { success: false, message: "Non authentifié." };
    }

    const { authorized } = requireRole(session, [GRP_ROLE_ADMIN]);
    if (!authorized) {
      return { success: false, message: "Accès refusé." };
    }

    const { ttlDays } = validateParams(saveDraftPurgeSettingSchema, params);
    const previous = await ensureDraftPurgeSetting();

    const saved = await prisma.draftPurgeSetting.upsert({
      where: { id: 1 },
      create: { id: 1, ttlDays },
      update: { ttlDays },
      select: { ttlDays: true },
    });

    await logActivity({
      userId: session.user.id,
      path: "SYSTEM",
      actionType: "DRAFT_UPDATE",
      metadata: `TTL brouillons : ${previous}j → ${saved.ttlDays}j`,
    });

    return { success: true, data: { ttlDays: saved.ttlDays } };
  } catch (error) {
    console.error("[saveDraftPurgeSettingAction]", error);
    return { success: false, message: "Une erreur serveur est survenue." };
  }
}
